<template>
	<div class="verify-email-page">
		<div class="verify-container">
			<!-- Карточка -->
			<div class="verify-card">
				<!-- Заголовок -->
				<div class="card-header">
					<h1 class="card-title">Подтвердите email</h1>
					<p class="card-subtitle">
						Мы отправили код на
						<span class="email-highlight">{{ maskedEmail }}</span>
					</p>
				</div>

				<!-- Форма ввода кода -->
				<form @submit.prevent="handleVerify" class="verify-form">
					<div class="code-input-group">
						<input
							v-for="(digit, index) in code"
							:key="index"
							:ref="el => (inputRefs[index] = el)"
							v-model="code[index]"
							type="text"
							maxlength="1"
							class="code-input"
							:class="{
								error: hasError,
								filled: code[index] !== '',
							}"
							@input="handleInput(index, $event)"
							@keydown="handleKeydown(index, $event)"
							@paste="handlePaste"
							@focus="activeIndex = index"
							@blur="activeIndex = -1"
							inputmode="numeric"
							pattern="[0-9]"
							:aria-label="`Цифра ${index + 1}`"
						/>
					</div>

					<!-- Ошибка -->
					<Transition name="fade">
						<p v-if="errorMessage" id="verify-msg" class="error-message">
							{{ errorMessage }}
						</p>
					</Transition>

					<!-- Кнопка -->
					<button
						type="submit"
						class="verify-button"
						:disabled="!isCodeComplete || isLoading"
					>
						<span v-if="!isLoading">Подтвердить</span>
						<span v-else class="loading-state">
							<span class="spinner"></span>
							Проверка...
						</span>
					</button>
				</form>

				<!-- Повторная отправка -->
				<div class="resend-section">
					<template v-if="canResend">
						<span class="resend-text">Не пришёл код?</span>
						<button
							@click="handleResend"
							class="resend-link"
							:disabled="isResending"
						>
							{{ isResending ? 'Отправка...' : 'Отправить ещё раз' }}
						</button>
					</template>
					<span v-else class="resend-timer">
						Отправить повторно через {{ resendTimer }}с
					</span>
				</div>

				<!-- Таймер кода -->
				<p class="code-expiry" :class="{ warning: codeExpiryMinutes <= 5 }">
					Код действителен {{ codeExpiryMinutes }} мин
				</p>
			</div>

			<!-- Ссылки -->
			<div class="footer-links">
				<button @click="handleBackToLogin" class="footer-link">
					Вернуться к входу
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import api from '@/services/api'
import { useAppStore } from '@/stores/useAppStore'

export default {
	name: 'VerifyEmail',
	setup() {
		const router = useRouter()
		const route = useRoute()
		const appStore = useAppStore()

		const code = ref(['', '', '', '', '', ''])
		const inputRefs = ref([])
		const email = ref(route.query.email || '')
		const errorMessage = ref('')
		const hasError = ref(false)
		const isLoading = ref(false)
		const isResending = ref(false)
		const canResend = ref(false)
		const resendTimer = ref(60)
		const activeIndex = ref(-1)
		const codeExpiryMinutes = ref(15)
		let timerInterval = null
		let expiryInterval = null

		const isCodeComplete = computed(() => {
			return code.value.every(digit => digit !== '')
		})

		const filledCount = computed(() => {
			return code.value.filter(digit => digit !== '').length
		})

		const maskedEmail = computed(() => {
			if (!email.value) return ''
			const [local, domain] = email.value.split('@')
			if (local.length <= 2) return email.value
			const masked =
				local[0] + '•'.repeat(Math.min(local.length - 2, 5)) + local.slice(-1)
			return `${masked}@${domain}`
		})

		const timerProgress = computed(() => {
			const circumference = 2 * Math.PI * 16
			const progress = (resendTimer.value / 60) * circumference
			return circumference - progress
		})

		const startResendTimer = () => {
			canResend.value = false
			resendTimer.value = 60

			timerInterval = setInterval(() => {
				resendTimer.value--
				if (resendTimer.value <= 0) {
					clearInterval(timerInterval)
					canResend.value = true
				}
			}, 1000)
		}

		const startExpiryTimer = () => {
			codeExpiryMinutes.value = 15
			expiryInterval = setInterval(() => {
				codeExpiryMinutes.value--
				if (codeExpiryMinutes.value <= 0) {
					clearInterval(expiryInterval)
				}
			}, 60000)
		}

		const handleInput = (index, event) => {
			const value = event.target.value

			if (!/^\d*$/.test(value)) {
				code.value[index] = ''
				return
			}

			code.value[index] = value

			if (value && index < 5) {
				inputRefs.value[index + 1]?.focus()
			}

			if (hasError.value) {
				hasError.value = false
				errorMessage.value = ''
			}
		}

		const handleKeydown = (index, event) => {
			if (event.key === 'Backspace' && !code.value[index] && index > 0) {
				inputRefs.value[index - 1]?.focus()
			}

			if (event.key === 'ArrowLeft' && index > 0) {
				inputRefs.value[index - 1]?.focus()
			}

			if (event.key === 'ArrowRight' && index < 5) {
				inputRefs.value[index + 1]?.focus()
			}
		}

		const handlePaste = event => {
			event.preventDefault()
			const pastedData = event.clipboardData.getData('text').trim()

			if (/^\d{6}$/.test(pastedData)) {
				code.value = pastedData.split('')
				inputRefs.value[5]?.focus()
			}
		}

		const handleVerify = async () => {
			if (!isCodeComplete.value || isLoading.value) return

			isLoading.value = true
			errorMessage.value = ''
			hasError.value = false

			try {
				console.log('📧 [VerifyEmail] Starting verification...')
				const codeString = code.value.join('')
				const response = await api.post('/auth/verify-email', {
					email: email.value,
					code: codeString,
				})

				console.log('✅ [VerifyEmail] Verification response:', response)

				const { accessToken, refreshToken } = response.data.tokens
				localStorage.setItem('access_token', accessToken)
				localStorage.setItem('refresh_token', refreshToken)

				console.log('💾 [VerifyEmail] Tokens saved to localStorage')

				const userResponse = await api.get('/auth/me')
				const userData = userResponse.data

				console.log('👤 [VerifyEmail] User data loaded:', userData)

				appStore.setUser(userData)
				appStore.persistUser()

				console.log('📦 [VerifyEmail] Store updated and persisted:', {
					isAuthenticated: appStore.isAuthenticated,
					user: appStore.user,
					localStorage_app_user: localStorage.getItem('app-user'),
				})

				const userRole = userData.roles?.[0] || 'student'
				let redirectPath = '/student-dashboard'

				if (userRole === 'teacher') {
					redirectPath = '/teacher/dashboard'
				} else if (userRole === 'admin') {
					redirectPath = '/admin/dashboard'
				}

				console.log('🔄 [VerifyEmail] Redirecting to:', redirectPath)
				await router.push(redirectPath)
			} catch (error) {
				hasError.value = true
				errorMessage.value =
					error.response?.data?.message || 'Неверный код. Попробуйте снова'

				code.value = ['', '', '', '', '', '']
				inputRefs.value[0]?.focus()
			} finally {
				isLoading.value = false
			}
		}

		const handleResend = async () => {
			if (isResending.value || !canResend.value) return

			isResending.value = true
			errorMessage.value = ''

			// helper только внутри handleResend
			const setMsg = (text, type = 'error', ms = 3000) => {
				errorMessage.value = text

				// ждём пока Vue отрисует <p>, затем меняем цвет
				setTimeout(() => {
					const el = document.getElementById('verify-msg')
					if (!el) return

					// success/info — не красный, error — красный
					el.style.color = type === 'error' ? '#dc2626' : '#0369a1'
					el.style.textAlign = 'center'
					el.style.fontSize = '13px'
				}, 0)

				// автоскрытие
				setTimeout(() => {
					// чтобы не стереть новое сообщение, если оно уже поменялось
					if (errorMessage.value === text) errorMessage.value = ''
				}, ms)
			}

			try {
				await api.post('/auth/send-verification-code', { email: email.value })

				startResendTimer()
				startExpiryTimer()

				// успех: показываем НЕ красным
				setMsg('Код отправлен повторно', 'info', 2500)
			} catch (error) {
				setMsg(
					error.response?.data?.message || 'Ошибка отправки кода',
					'error',
					4000,
				)
			} finally {
				isResending.value = false
			}
		}

		const handleChangeEmail = () => {
			router.push('/auth?mode=register')
		}

		const handleBackToLogin = () => {
			router.push('/auth')
		}

		onMounted(() => {
			if (!email.value) {
				router.push('/auth')
				return
			}

			inputRefs.value[0]?.focus()

			startResendTimer()
			startExpiryTimer()
		})

		onUnmounted(() => {
			if (timerInterval) {
				clearInterval(timerInterval)
			}
			if (expiryInterval) {
				clearInterval(expiryInterval)
			}
		})

		return {
			code,
			inputRefs,
			email,
			errorMessage,
			hasError,
			isLoading,
			isResending,
			canResend,
			resendTimer,
			isCodeComplete,
			activeIndex,
			filledCount,
			maskedEmail,
			timerProgress,
			codeExpiryMinutes,
			handleInput,
			handleKeydown,
			handlePaste,
			handleVerify,
			handleResend,
			handleChangeEmail,
			handleBackToLogin,
		}
	},
}
</script>

<style scoped>
.verify-email-page {
	min-height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f0f9ff;
	padding: 24px;
}

.verify-container {
	width: 100%;
	max-width: 380px;
}

/* Карточка */
.verify-card {
	background: white;
	border-radius: 12px;
	padding: 32px 28px;
	box-shadow: 0 1px 3px rgba(3, 105, 161, 0.1);
	border: 1px solid #bae6fd;
}

/* Заголовок */
.card-header {
	text-align: center;
	margin-bottom: 28px;
}

.card-title {
	font-size: 20px;
	font-weight: 600;
	color: #0c4a6e;
	margin: 0 0 8px;
}

.card-subtitle {
	font-size: 14px;
	color: #0369a1;
	margin: 0;
	line-height: 1.5;
}

.email-highlight {
	color: #075985;
	font-weight: 500;
}

/* Форма */
.verify-form {
	margin-bottom: 20px;
}

.code-input-group {
	display: flex;
	gap: 8px;
	justify-content: center;
	margin-bottom: 20px;
}

.code-input {
	width: 46px;
	height: 54px;
	font-size: 22px;
	font-weight: 600;
	text-align: center;
	border: 1.5px solid #bae6fd;
	border-radius: 8px;
	background: #f0f9ff;
	color: #0c4a6e;
	transition: all 0.15s;
	outline: none;
}

.code-input:focus {
	border-color: #0284c7;
	background: white;
	box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.15);
}

.code-input.filled {
	border-color: #0ea5e9;
	background: white;
}

.code-input.error {
	border-color: #dc2626;
	background: #fef2f2;
	animation: shake 0.3s;
}

@keyframes shake {
	0%,
	100% {
		transform: translateX(0);
	}
	25% {
		transform: translateX(-3px);
	}
	75% {
		transform: translateX(3px);
	}
}

/* Ошибка */
.error-message {
	text-align: center;
	color: #dc2626;
	font-size: 13px;
	margin: 0 0 16px;
}

/* Кнопка */
.verify-button {
	width: 100%;
	height: 46px;
	background: linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%);
	color: white;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s;
}

.verify-button:hover:not(:disabled) {
	background: linear-gradient(135deg, #075985 0%, #0284c7 100%);
	box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.verify-button:disabled {
	background: #7dd3fc;
	cursor: not-allowed;
}

.loading-state {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
}

.spinner {
	width: 14px;
	height: 14px;
	border: 2px solid rgba(255, 255, 255, 0.3);
	border-top-color: white;
	border-radius: 50%;
	animation: spin 0.7s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

/* Повторная отправка */
.resend-section {
	text-align: center;
	padding-top: 20px;
	border-top: 1px solid #e0f2fe;
}

.resend-text {
	font-size: 13px;
	color: #0369a1;
}

.resend-link {
	background: none;
	border: none;
	color: #0284c7;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	margin-left: 4px;
	text-decoration: underline;
	text-underline-offset: 2px;
}

.resend-link:hover:not(:disabled) {
	color: #0369a1;
}

.resend-link:disabled {
	color: #7dd3fc;
	cursor: not-allowed;
}

.resend-timer {
	font-size: 13px;
	color: #0369a1;
}

/* Таймер кода */
.code-expiry {
	text-align: center;
	font-size: 12px;
	color: #38bdf8;
	margin: 12px 0 0;
}

.code-expiry.warning {
	color: #d97706;
}

/* Ссылки */
.footer-links {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 12px;
	margin-top: 20px;
}

.footer-link {
	background: none;
	border: none;
	color: #0369a1;
	font-size: 13px;
	cursor: pointer;
	padding: 4px;
	transition: color 0.15s;
}

.footer-link:hover {
	color: #0284c7;
}

.divider {
	color: #bae6fd;
	font-size: 8px;
}

/* Анимации */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

/* Адаптивность */
@media (max-width: 440px) {
	.verify-email-page {
		padding: 16px;
		align-items: flex-start;
		padding-top: 60px;
	}

	.verify-card {
		padding: 24px 20px;
	}

	.code-input {
		width: 42px;
		height: 50px;
		font-size: 20px;
	}

	.code-input-group {
		gap: 6px;
	}
}

@media (max-width: 360px) {
	.code-input {
		width: 38px;
		height: 46px;
		font-size: 18px;
	}

	.code-input-group {
		gap: 4px;
	}
}
</style>
